import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getTours, getBlogPosts, getSliderImages, getTourCalendar } from '../services/api';
import { Calendar, Users, MapPin, Star, Clock, CheckCircle2, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import SEO from '../components/SEO';
import { useLanguage } from '../contexts/LanguageContext';
import { tourCurrencySymbol, localeFor, parseISODate, formatTourDate } from '@/lib/utils';
import { TourDateBadge } from '../components/TourDateBadge';
import { trackEvent } from '@/lib/analytics';

// Default slides (fallback)
const defaultSlides = [
  {
    image_url: 'https://images.pexels.com/photos/3264723/pexels-photo-3264723.jpeg?w=1920',
    title: "İstanbul'u Yürüyerek Keşfedin",
    subtitle: 'Profesyonel rehber eşliğinde benzersiz İstanbul yürüyüş turları'
  },
  {
    image_url: 'https://images.pexels.com/photos/2087391/pexels-photo-2087391.jpeg?w=1920',
    title: "Balat'ın Renkli Sokaklarında",
    subtitle: 'Tarihi mahallelerde kültür ve mimari yolculuğu'
  },
  {
    image_url: 'https://images.pexels.com/photos/3732500/pexels-photo-3732500.jpeg?w=1920',
    title: 'Boğaz Manzaralı Turlar',
    subtitle: 'İstanbul siluetinin en güzel noktalarını keşfedin'
  },
  {
    image_url: 'https://images.pexels.com/photos/2404046/pexels-photo-2404046.jpeg?w=1920',
    title: 'Tarihi Çarşılar ve Hanlar',
    subtitle: 'Osmanlı ticaret tarihinin izlerini sürün'
  }
];

export default function HomePage() {
  const { language, t } = useLanguage();
  const [tours, setTours] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [heroSlides, setHeroSlides] = useState(defaultSlides);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  }); // Current month
  const [selectedDay, setSelectedDay] = useState(null);
  const shiftMonth = (delta) => {
    setSelectedDay(null);
    setSelectedMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + delta, 1));
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [toursRes, blogRes, sliderRes, calendarRes] = await Promise.all([
          getTours({ lang: language, tour_type: 'istanbul' }),
          getBlogPosts({ lang: language }),
          getSliderImages(language),
          getTourCalendar(language)
        ]);
        const withDateFirst = [...toursRes.data].sort((x, y) => (x.next_departure || '9999').localeCompare(y.next_departure || '9999'));
        setTours(withDateFirst.slice(0, 6));
        setBlogPosts(blogRes.data.slice(0, 3));
        
        // Use DB sliders if available, otherwise use defaults
        if (sliderRes.data && sliderRes.data.length > 0) {
          setHeroSlides(sliderRes.data);
        }
        
        const istanbulEvents = (calendarRes.data || []).filter(e => e.tour_type === 'istanbul');
        setCalendarEvents(istanbulEvents);
        
        // Start at current month; if it has no tours, jump forward to the first month that has
        const events = istanbulEvents;
        const now = new Date();
        const currentMonthStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
        const firstUpcoming = events.map(e => e.date).filter(d => d.slice(0, 7) >= currentMonthStr).sort()[0];
        if (firstUpcoming && !firstUpcoming.startsWith(currentMonthStr)) {
          const [year, month] = firstUpcoming.split('-').map(Number);
          setSelectedMonth(new Date(year, month - 1, 1));
        } else {
          setSelectedMonth(new Date(now.getFullYear(), now.getMonth(), 1));
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [language]); // Re-fetch when language changes

  // Auto slide
  useEffect(() => {
    if (heroSlides.length === 0) return;
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [heroSlides.length]);

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);

  return (
    <div className="bg-gray-50">
      <SEO 
        fullTitle="İstanbul Turda | Rehberli İstanbul Turları"
        description="İstanbul'un tarihini, kültürünü ve eşsiz güzelliklerini profesyonel turist rehberleri eşliğinde keşfedin. İstanbul yürüyüş turlarımızı inceleyin ve hemen rezervasyon yapın."
      />
      
      {/* Hero Section with Slider */}
      <section className="relative h-[600px] md:h-[700px] overflow-hidden" data-testid="hero-section">
        {/* Slides */}
        {heroSlides.map((slide, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentSlide ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <div
              className="absolute inset-0 bg-cover bg-center bg-no-repeat"
              style={{ backgroundImage: `url('${slide.image_url || slide.image}')` }}
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent"></div>
          </div>
        ))}
        
        {/* Content */}
        <div className="relative z-10 h-full flex items-center">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 w-full">
            <div className="max-w-2xl">
              <h1 
                className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight transition-all duration-500" 
                data-testid="hero-title"
                key={currentSlide}
              >
                {heroSlides[currentSlide].title}
              </h1>
              <p 
                className="text-lg md:text-xl text-gray-200 mb-8 transition-all duration-500" 
                data-testid="hero-subtitle"
              >
                {heroSlides[currentSlide].subtitle}
              </p>
              <Link to="/turlar/istanbul">
                <Button
                  size="lg"
                  className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-6 text-lg rounded-full font-bold font-ui tracking-wide shadow-lg shadow-orange-500/30 transition-all hover:scale-105 active:scale-95"
                  data-testid="hero-cta-button"
                >
                  Turları Gör
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Navigation Arrows */}
        <button
          onClick={prevSlide}
          className="absolute left-4 top-1/2 -translate-y-1/2 z-20 bg-white/20 hover:bg-white/40 text-white p-3 rounded-full backdrop-blur-sm transition-all"
          data-testid="slider-prev"
        >
          <ChevronLeft size={24} />
        </button>
        <button
          onClick={nextSlide}
          className="absolute right-4 top-1/2 -translate-y-1/2 z-20 bg-white/20 hover:bg-white/40 text-white p-3 rounded-full backdrop-blur-sm transition-all"
          data-testid="slider-next"
        >
          <ChevronRight size={24} />
        </button>

        {/* Dots */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-3">
          {heroSlides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-3 h-3 rounded-full transition-all ${
                index === currentSlide ? 'bg-orange-500 w-8' : 'bg-white/50 hover:bg-white/80'
              }`}
              data-testid={`slider-dot-${index}`}
            />
          ))}
        </div>
      </section>

      {/* Search Section */}
      <section className="-mt-16 relative z-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-10" data-testid="search-section">
            <h2 className="text-2xl font-bold mb-6 text-gray-900">Tur Kategorileri</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Link to="/turlar/istanbul" className="group">
                <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl p-6 text-center hover:shadow-lg transition-all hover:-translate-y-1">
                  <MapPin className="mx-auto mb-3" size={32} />
                  <h3 className="text-xl font-bold mb-1">İstanbul Turları</h3>
                  <p className="text-blue-100 text-sm">15 farklı tur rotası</p>
                </div>
              </Link>
              <Link to="/turlar/yurtici" className="group">
                <div className="bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl p-6 text-center hover:shadow-lg transition-all hover:-translate-y-1">
                  <MapPin className="mx-auto mb-3" size={32} />
                  <h3 className="text-xl font-bold mb-1">Yurtiçi Turlar</h3>
                  <p className="text-green-100 text-sm">Kapadokya, Efes ve daha fazlası</p>
                </div>
              </Link>
              <Link to="/turlar/yurtdisi" className="group">
                <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-xl p-6 text-center hover:shadow-lg transition-all hover:-translate-y-1">
                  <MapPin className="mx-auto mb-3" size={32} />
                  <h3 className="text-xl font-bold mb-1">Yurtdışı Turlar</h3>
                  <p className="text-purple-100 text-sm">Yunanistan, Balkanlar</p>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Tour Calendar Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              <Calendar className="inline-block mr-3 text-orange-500" size={36} />
              {t('home.istanbul_calendar')}
            </h2>
            <p className="text-lg text-gray-600">{t('home.calendar_desc')}</p>
          </div>
          
          <div className="bg-gray-50 rounded-2xl p-6 md:p-8">
            {/* Month Navigation */}
            <div className="flex justify-between items-center mb-6">
              <button 
                onClick={() => shiftMonth(-1)}
                className="p-2 hover:bg-gray-200 rounded-full transition-colors"
                data-testid="calendar-prev-month"
              >
                <ChevronLeft size={24} />
              </button>
              <h3 className="text-xl font-bold text-gray-900 capitalize" data-testid="calendar-month-title">
                {selectedMonth.toLocaleDateString(localeFor(language), { month: 'long', year: 'numeric' })}
              </h3>
              <button 
                onClick={() => shiftMonth(1)}
                className="p-2 hover:bg-gray-200 rounded-full transition-colors"
                data-testid="calendar-next-month"
              >
                <ChevronRight size={24} />
              </button>
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-1 mb-4">
              {[...Array(7)].map((_, i) => new Date(2024, 0, 1 + i).toLocaleDateString(localeFor(language), { weekday: 'short' })).map(day => (
                <div key={day} className="text-center text-xs md:text-sm font-bold text-gray-500 py-2">{day}</div>
              ))}
            </div>
            
            <div className="grid grid-cols-7 gap-1">
              {(() => {
                const year = selectedMonth.getFullYear();
                const month = selectedMonth.getMonth();
                const firstDay = new Date(year, month, 1);
                const lastDay = new Date(year, month + 1, 0);
                const startPadding = (firstDay.getDay() + 6) % 7;
                const days = [];
                
                // Padding days
                for (let i = 0; i < startPadding; i++) {
                  days.push(<div key={`pad-${i}`} className="h-16 md:h-24 bg-gray-100 rounded-lg"></div>);
                }
                
                // Actual days
                for (let day = 1; day <= lastDay.getDate(); day++) {
                  const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                  const dayEvents = calendarEvents.filter(e => e.date === dateStr);
                  const isToday = new Date().toDateString() === new Date(year, month, day).toDateString();
                  
                  days.push(
                    <div 
                      key={day} 
                      onClick={() => dayEvents.length > 0 && setSelectedDay(dateStr)}
                      data-testid={`calendar-day-${dateStr}`}
                      className={`min-h-16 md:min-h-24 p-1 md:p-2 rounded-lg border transition-colors ${dayEvents.length > 0 ? 'cursor-pointer' : ''} ${
                        selectedDay === dateStr ? 'bg-orange-100 border-orange-500 ring-2 ring-orange-300' :
                        isToday ? 'bg-orange-50 border-orange-300' : 'bg-white border-gray-200 hover:border-orange-300'
                      }`}
                    >
                      <div className={`text-xs md:text-sm font-bold mb-1 ${isToday ? 'text-orange-500' : 'text-gray-700'}`}>
                        {day}
                      </div>
                      {/* Desktop: show event titles */}
                      <div className="hidden md:block space-y-1">
                        {dayEvents.slice(0, 2).map(event => (
                          <Link 
                            key={event.id}
                            to={`/turlar/${event.slug}`}
                            onClick={(e) => e.stopPropagation()}
                            className={`block text-xs p-1 rounded truncate ${
                              event.tour_type === 'istanbul' ? 'bg-blue-100 text-blue-700' :
                              event.tour_type === 'yurtici' ? 'bg-green-100 text-green-700' :
                              'bg-purple-100 text-purple-700'
                            }`}
                          >
                            {event.title.substring(0, 15)}...
                          </Link>
                        ))}
                        {dayEvents.length > 2 && (
                          <div className="text-xs text-gray-500">+{dayEvents.length - 2} daha</div>
                        )}
                      </div>
                      {/* Mobile: show dots only */}
                      <div className="md:hidden flex flex-wrap gap-0.5">
                        {dayEvents.slice(0, 3).map((event, idx) => (
                          <div 
                            key={idx}
                            className={`w-2 h-2 rounded-full ${
                              event.tour_type === 'istanbul' ? 'bg-blue-500' :
                              event.tour_type === 'yurtici' ? 'bg-green-500' :
                              'bg-purple-500'
                            }`}
                          />
                        ))}
                        {dayEvents.length > 3 && (
                          <span className="text-[10px] text-gray-500">+</span>
                        )}
                      </div>
                    </div>
                  );
                }
                
                return days;
              })()}
            </div>

            {selectedDay && (
              <div className="mt-6 bg-white rounded-xl border border-orange-200 p-4" data-testid="calendar-day-panel">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-bold text-gray-900 capitalize">{t('home.day_tours')}: {formatTourDate(selectedDay, language)}</h4>
                  <button onClick={() => setSelectedDay(null)} className="text-sm text-gray-500 hover:text-gray-800" data-testid="calendar-day-panel-close">✕</button>
                </div>
                <div className="space-y-2">
                  {calendarEvents.filter(e => e.date === selectedDay).map(event => (
                    <Link key={event.id} to={`/turlar/${event.slug}`} className="flex items-center justify-between gap-3 rounded-lg border border-gray-100 p-3 hover:border-orange-300 transition-colors" data-testid={`calendar-day-tour-${event.slug}`}>
                      <span className="font-semibold text-gray-900 text-sm line-clamp-2">{event.title}</span>
                      <span className="flex items-center gap-2 flex-shrink-0">
                        <span className={`text-xs font-semibold rounded-full px-2 py-0.5 ${event.availability === 'full' ? 'bg-red-100 text-red-700' : event.availability === 'limited' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>
                          {event.availability === 'full' ? t('tour.full') : event.availability === 'limited' ? t('tour.limited') : t('tour.available')}
                        </span>
                        <span className="text-orange-600 font-bold text-sm">{tourCurrencySymbol(event)}{event.price}</span>
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            )}

          </div>
        </div>
      </section>

      {/* Featured Tours */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4" data-testid="featured-tours-title">
              {t('home.featured_tours')}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t('home.featured_subtitle')}
            </p>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-white rounded-2xl overflow-hidden border border-gray-100 animate-pulse">
                  <div className="h-48 md:h-64 bg-gray-200"></div>
                  <div className="p-4 md:p-6">
                    <div className="h-6 bg-gray-200 rounded mb-4"></div>
                    <div className="h-4 bg-gray-200 rounded mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8" data-testid="tours-grid">
              {tours.map((tour) => (
                <Link
                  to={`/turlar/${tour.slug}`}
                  key={tour.id}
                  className="group bg-white rounded-2xl overflow-hidden border border-gray-100 transition-all hover:-translate-y-1 hover:shadow-xl active:scale-[0.98]"
                  data-testid={`tour-card-${tour.slug}`}
                >
                  <div className="relative overflow-hidden h-48 md:h-64">
                    <img
                      src={tour.image_url || 'https://images.pexels.com/photos/3264723/pexels-photo-3264723.jpeg?w=800'}
                      alt={tour.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      loading="lazy"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'https://images.pexels.com/photos/3264723/pexels-photo-3264723.jpeg?w=800';
                      }}
                    />
                    <TourDateBadge tour={tour} />
                    {tour.discount_percentage > 0 && (
                      <div className="absolute top-3 right-3 md:top-4 md:right-4 bg-red-500 text-white px-2 py-1 md:px-3 rounded-full text-xs md:text-sm font-ui font-bold">
                        %{tour.discount_percentage} İndirim
                      </div>
                    )}
                    {/* Tour type badge on mobile */}
                    <div className={`absolute top-3 left-3 md:hidden px-2 py-1 rounded-full text-xs font-bold ${
                      tour.tour_type === 'istanbul' ? 'bg-blue-100 text-blue-700' :
                      tour.tour_type === 'yurtici' ? 'bg-green-100 text-green-700' :
                      'bg-purple-100 text-purple-700'
                    }`}>
                      {tour.tour_type === 'istanbul' ? 'İstanbul' :
                       tour.tour_type === 'yurtici' ? 'Yurtiçi' : 'Yurtdışı'}
                    </div>
                  </div>
                  <div className="p-4 md:p-6">
                    <div className="flex items-center space-x-2 text-xs md:text-sm text-gray-500 mb-2 md:mb-3">
                      <MapPin size={14} className="md:w-4 md:h-4" />
                      <span className="truncate">{tour.category}</span>
                      <span className="text-gray-300">•</span>
                      <Clock size={14} className="md:w-4 md:h-4" />
                      <span>{tour.duration}</span>
                    </div>
                    <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2 md:mb-3 group-hover:text-orange-500 transition-colors line-clamp-2">
                      {tour.title}
                    </h3>
                    <p className="text-gray-600 mb-3 md:mb-4 line-clamp-2 text-sm md:text-base">{tour.short_description}</p>
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-xl md:text-2xl font-bold font-ui text-orange-500">
                          {tourCurrencySymbol(tour)}{tour.price}
                        </span>
                        <span className="text-xs md:text-sm text-gray-500 ml-1">{t('tours.per_person')}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            trackEvent('whatsapp_click', { tour_slug: tour.slug });
                            window.open(`https://wa.me/905414112373?text=${encodeURIComponent(`Merhaba, "${tour.title}" turu hakkında bilgi almak istiyorum.`)}`, '_blank');
                          }}
                          className="bg-green-500 hover:bg-green-600 text-white p-2 rounded-full transition-colors"
                          data-testid={`whatsapp-${tour.slug}`}
                          aria-label="WhatsApp ile iletişim"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                          </svg>
                        </button>
                        <Button
                          className="bg-orange-500 hover:bg-orange-600 text-white rounded-full font-ui text-xs md:text-sm px-3 md:px-4 py-2"
                          data-testid={`book-tour-${tour.slug}`}
                        >
                          Detay
                        </Button>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}

          <div className="text-center mt-12">
            <Link to="/turlar/istanbul">
              <Button
                variant="outline"
                size="lg"
                className="border-2 border-gray-900 hover:bg-gray-900 hover:text-white rounded-full px-8 font-ui font-bold"
                data-testid="view-all-tours-button"
              >
                {t('home.view_all_tours')}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Bu Ay Turlarımız - Dinamik */}
      <section className="py-24 md:py-32 bg-gradient-to-b from-orange-50 to-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4" data-testid="upcoming-tours-title">
              {t('home.upcoming_istanbul')}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto capitalize" data-testid="upcoming-tours-month">
              {selectedMonth.toLocaleDateString(localeFor(language), { month: 'long', year: 'numeric' })} {t('home.month_calendar')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {(() => {
              const monthStr = `${selectedMonth.getFullYear()}-${String(selectedMonth.getMonth() + 1).padStart(2, '0')}`;
              const monthEvents = calendarEvents
                .filter(e => e.date.startsWith(monthStr))
                .sort((a, b) => a.date.localeCompare(b.date))
                .slice(0, 6);
              
              if (monthEvents.length === 0) {
                return (
                  <div className="col-span-full text-center py-12">
                    <p className="text-gray-500 text-lg">{t('home.no_tours_this_month')}</p>
                    <p className="text-gray-400 mt-2">{t('home.check_calendar')}</p>
                  </div>
                );
              }
              
              return monthEvents.map((event, idx) => {
                const eventDate = parseISODate(event.date);
                const day = eventDate.getDate();
                const monthName = eventDate.toLocaleDateString(localeFor(language), { month: 'short' }).toLocaleUpperCase(localeFor(language));
                const currency = tourCurrencySymbol(event);
                
                return (
                  <div key={`${event.id}-${idx}`} className="bg-white border-2 border-orange-200 rounded-2xl p-6 hover:shadow-xl transition-all hover:-translate-y-1">
                    <div className="flex items-start gap-4">
                      <div className={`text-white rounded-xl px-4 py-3 text-center min-w-[80px] ${
                        event.tour_type === 'istanbul' ? 'bg-blue-500' :
                        event.tour_type === 'yurtici' ? 'bg-green-500' :
                        'bg-purple-500'
                      }`}>
                        <div className="text-3xl font-bold font-ui">{String(day).padStart(2, '0')}</div>
                        <div className="text-sm uppercase">{monthName}</div>
                      </div>
                      <div className="flex-1">
                        <h3 className="font-bold text-lg text-gray-900 mb-2 line-clamp-2">{event.title}</h3>
                        <p className="flex items-center gap-1.5 text-sm font-semibold text-orange-600 mb-3 capitalize" data-testid={`upcoming-tour-date-${event.id}`}>
                          <Calendar size={14} /> {formatTourDate(event.date, language)}
                        </p>
                        <div className="flex items-center justify-between">
                          <span className="text-orange-600 font-bold font-ui">{currency}{event.price}</span>
                          <Link to={`/turlar/${event.slug}`}>
                            <Button size="sm" className="bg-orange-500 hover:bg-orange-600 text-white rounded-full">{event.availability === 'full' ? t('tour.full') : t('home.book')}</Button>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              });
            })()}
          </div>

          <div className="text-center mt-12">
            <Link to="/turlar">
              <Button size="lg" className="bg-gray-900 hover:bg-gray-800 text-white rounded-full px-8 font-ui font-bold">
                {t('home.view_all_tours')}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">Neden Bizi Seçmelisiniz?</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              İstanbul'da en iyi tur deneyimini sunmak için çalışıyoruz
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="p-8 rounded-2xl bg-gray-50 hover:bg-white hover:shadow-xl transition-all border border-transparent hover:border-gray-100 text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Users className="text-orange-500" size={32} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Profesyonel Rehberler</h3>
              <p className="text-gray-600">
                Deneyimli ve lisanslı rehberlerimizle unutulmaz bir deneyim
              </p>
            </div>

            <div className="p-8 rounded-2xl bg-gray-50 hover:bg-white hover:shadow-xl transition-all border border-transparent hover:border-gray-100 text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Star className="text-orange-500" size={32} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Mükemmel Hizmet</h3>
              <p className="text-gray-600">Misafirlerimizin memnuniyeti bizim için her şeyden önemli</p>
            </div>

            <div className="p-8 rounded-2xl bg-gray-50 hover:bg-white hover:shadow-xl transition-all border border-transparent hover:border-gray-100 text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Calendar className="text-orange-500" size={32} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Esnek Tarihler</h3>
              <p className="text-gray-600">Sizin için uygun olan her tarihte turlarımız mevcut</p>
            </div>

            <div className="p-8 rounded-2xl bg-gray-50 hover:bg-white hover:shadow-xl transition-all border border-transparent hover:border-gray-100 text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="text-orange-500" size={32} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Güvenli Rezervasyon</h3>
              <p className="text-gray-600">Hızlı ve güvenli rezervasyon sistemi ile kolay rezervasyon</p>
            </div>
          </div>
        </div>
      </section>

      {/* About Istanbul */}
      <section className="py-24 md:py-32 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                İstanbul'un Büyüsünü Keşfedin
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                İki kıtayı birleştiren bu eşsiz şehir, binlerce yıllık tarihi, muhteşem mimarisi ve
                zengin kültürelü ile dünya’nın en büyüleyici destinasyonlarından biridir.
              </p>
              <p className="text-lg text-gray-600 mb-8">
                Yürüyüş turlarımızla Sultanahmet'in tarihi dokusundan Boğaz'ın eşsiz manzaralarına,
                Kadıköy'ün renkli sokaklarından Kapalıçarşı'nın gizemli dükkanlarına kadar İstanbul'u
                baştan sona keşfedin.
              </p>
              <Link to="/hakkimizda">
                <Button
                  size="lg"
                  className="bg-orange-500 hover:bg-orange-600 text-white rounded-full font-ui font-bold px-8"
                >
                  Hakkımızda Daha Fazla
                </Button>
              </Link>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1760899989033-a69990bf6d20?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHw0fHxJc3RhbmJ1bCUyMEdhbGF0YSUyMFRvd2VyJTIwc3Vuc2V0JTIwbGFuZHNjYXBlfGVufDB8fHx8MTc3MjE4OTYzNXww&ixlib=rb-4.1.0&q=85"
                alt="İstanbul"
                className="rounded-2xl shadow-2xl"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Blog Section */}
      {blogPosts.length > 0 && (
        <section className="py-24 md:py-32 bg-white">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">İstanbul Hikayeleri</h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                İstanbul'un tarihi, kültürü ve gizli köşelerini keşfedin
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {blogPosts.map((post) => (
                <Link
                  to={`/istanbul-hikayeleri/${post.slug}`}
                  key={post.id}
                  className="group bg-white rounded-2xl overflow-hidden border border-gray-100 transition-all hover:-translate-y-1 hover:shadow-xl"
                >
                  <div className="relative overflow-hidden h-56">
                    <img
                      src={post.image_url || 'https://images.pexels.com/photos/3264723/pexels-photo-3264723.jpeg?w=800'}
                      alt={post.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      loading="lazy"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'https://images.pexels.com/photos/3264723/pexels-photo-3264723.jpeg?w=800';
                      }}
                    />
                  </div>
                  <div className="p-6">
                    <div className="text-sm text-orange-500 font-ui font-bold mb-2">{post.category}</div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-orange-500 transition-colors">
                      {post.title}
                    </h3>
                    <p className="text-gray-600 line-clamp-3">{post.excerpt}</p>
                  </div>
                </Link>
              ))}
            </div>

            <div className="text-center mt-12">
              <Link to="/istanbul-hikayeleri">
                <Button
                  variant="outline"
                  size="lg"
                  className="border-2 border-gray-900 hover:bg-gray-900 hover:text-white rounded-full px-8 font-ui font-bold"
                >
                  Tüm Hikayeler
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
